import { useCallback, useEffect, useState } from 'react'; // useCallback retained for fetchData/saveData/deleteData
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import type { UserDto } from '../../api/models/UserDto';
import type { TokenSummaryResponse } from '../../api/models/TokenSummaryResponse';
import { AdminServiceTokenControllerService } from '../../api/services/AdminServiceTokenControllerService';
import { ResourceControllerService } from '../../api/services/ResourceControllerService';
import { CrudPage } from '../../components/CrudPage';
import NewTokenDialog, { type ResourceOption } from './NewTokenDialog';
import { useErrorHandler } from '../../utils/errorHandler';

type Props = {
  user: UserDto;
  onClose: () => void;
};

type TokenRow = TokenSummaryResponse & { id: string };

function formatDateTime(iso: string | undefined, locale: string): string {
  if (!iso) {
    return '–';
  }
  return new Date(iso).toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Outer dialog for the mobile-token management of a single technical user.
 * The list itself + the create flow are driven by an embedded CrudPage in
 * localMode-style configuration — same pattern as OperationDetailDialog →
 * InputQuantitySubDialog. CrudPage owns the open/close lifecycle of the
 * NewTokenDialog FormComponent, which avoids the nested-dialog cascade-close
 * issues we hit when managing the sub-dialog visibility manually.
 */
export default function MobileTokensDialog({ user, onClose }: Props) {
  const { t, i18n } = useTranslation(['common']);
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const { handleError } = useErrorHandler();
  const [availableResources, setAvailableResources] = useState<ResourceOption[]>([]);

  // Load resources once at the outer dialog level so that the inner CrudPage
  // and its FormComponent always see the populated list at mount time. The
  // CrudPage rendering is gated on resourcesReady below — see the
  // feedback_syncfusion_nested_dialog memo for why this gate is necessary.
  useEffect(() => {
    let cancelled = false;
    ResourceControllerService.findAll()
      .then((list) => {
        if (cancelled) {
          return;
        }
        const next = (list ?? [])
          .filter((r): r is { uuid: string; name: string } => Boolean(r.uuid && r.name))
          .map((r) => ({ uuid: r.uuid, name: r.name }));
        setAvailableResources(next);
      })
      .catch((e) => handleError(e, 'loading resources'));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async (): Promise<TokenRow[]> => {
    if (!user.uuid) {
      return [];
    }
    try {
      const list = await AdminServiceTokenControllerService.listTokens(user.uuid);
      return list.map((tok) => ({ ...tok, id: String(tok.tokenId ?? tok.tokenUuid ?? '') }));
    } catch (e) {
      handleError(e, 'listing service tokens');
      return [];
    }
  }, [user.uuid, handleError]);

  // No-op: the actual create call lives in NewTokenDialog so the QR-code
  // step can render before the dialog closes. CrudPage still triggers a
  // refresh after onSave, which is what we need.
  const saveData = useCallback(async (_row: TokenRow): Promise<void> => {
    // intentionally empty
  }, []);

  const deleteData = useCallback(async (id: string): Promise<void> => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) {
      return;
    }
    try {
      await AdminServiceTokenControllerService.revokeToken(numericId);
    } catch (e) {
      handleError(e, 'revoking service token');
      throw e;
    }
  }, [handleError]);

  const footerTemplate = () => (
    <div className="flex justify-end gap-2 p-4">
      <ButtonComponent
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => onClose();
          }
        }}
      >
        {t('actions.close')}
      </ButtonComponent>
    </div>
  );

  // CrudPage gets mounted only AFTER the resource list has been loaded —
  // otherwise its closures (FormComponent etc.) snap to the initial empty
  // availableResources, and Syncfusion's content prop pins them to that
  // first render. Same gate-on-loaded pattern as
  // OperationDetailDialog (loading flag → CrudPage).
  const resourcesReady = availableResources.length > 0;

  // Intentionally NOT useCallback: see resourcesReady comment above. Children
  // (not content prop) are needed so React reconciles on every render.
  const dialogContent = () => (
    <div className="h-full flex flex-col">
      {!resourcesReady ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
      <CrudPage<TokenRow>
        titleKey="mobileTokens.title"
        showTitle={false}
        noPadding={true}
        gridMeta={[
          {
            field: 'label',
            headerKey: 'mobileTokens.columns.label',
            width: 200,
            render: (row: any) => row.label || t('mobileTokens.unlabeled'),
          },
          {
            field: 'kind',
            headerKey: 'mobileTokens.columns.kind',
            width: 200,
            render: (row: any) => {
              const count = row.scope?.length ?? 0;
              return count <= 1
                ? t('mobileTokens.operatorKind')
                : t('mobileTokens.shiftLeaderKind', { count });
            },
          },
          {
            field: 'scope',
            headerKey: 'mobileTokens.columns.resources',
            width: 220,
            render: (row: any) =>
              (row.scope ?? [])
                .map((s: any) => s.name)
                .filter(Boolean)
                .join(', ') || '–',
          },
          {
            field: 'lastUsedAt',
            headerKey: 'mobileTokens.columns.lastUsed',
            width: 140,
            render: (row: any) => formatDateTime(row.lastUsedAt, locale),
          },
          {
            field: 'expiresAt',
            headerKey: 'mobileTokens.columns.expires',
            width: 140,
            render: (row: any) => formatDateTime(row.expiresAt, locale),
          },
        ]}
        fetchData={fetchData}
        saveData={saveData}
        deleteData={deleteData}
        // Wrap FormComponent inline so the parent-loaded availableResources is
        // captured by closure — same pattern as OperationDetailDialog →
        // InputQuantitySubDialog.
        FormComponent={({ value, onClose: closeFn, onSave: saveFn }: any) => (
          <NewTokenDialog
            userUuid={user.uuid ?? ''}
            availableResources={availableResources}
            value={value}
            onClose={closeFn}
            onSave={saveFn}
          />
        )}
        showAddButton={true}
        showEditButton={false}
        showDeleteButton={true}
        showSearchButton={false}
        allowFiltering={false}
        allowSorting={false}
        entityName="service-token"
      />
      )}
    </div>
  );

  return (
    <DialogComponent
      visible={true}
      isModal={true}
      header={t('mobileTokens.title', { user: user.username })}
      showCloseIcon={true}
      closeOnEscape={true}
      width="1100px"
      height="720px"
      close={onClose}
      target="body"
      animationSettings={{ effect: 'None' }}
      cssClass="mobile-tokens-dialog"
      zIndex={10000}
      footerTemplate={footerTemplate}
    >
      {dialogContent()}
    </DialogComponent>
  );
}
